import { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Form,
  Input,
  Typography,
  Tag
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { withDevModalFilePath } from '../../components/common';

const { Modal } = withDevModalFilePath('client/src/pages/settings/SystemConfigPage.tsx');
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useSystemConfigs, useSystemConfigMutations } from '../../hooks/useSystemConfig';

const { Title, Text } = Typography;
const { TextArea } = Input;

type ConfigRecord = ReturnType<typeof useSystemConfigs>['configs'][number];

const stringifyValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const parseValue = (input: string): unknown => {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return '';
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return input;
  }
};

export const SystemConfigPage = (): JSX.Element => {
  const { configs, isLoading, refetch } = useSystemConfigs();
  const { setConfig, deleteConfig, isSaving, isDeleting } = useSystemConfigMutations();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<ConfigRecord | null>(null);
  const [form] = Form.useForm();

  const sortedConfigs = useMemo(
    () => [...configs].sort((a, b) => a.configKey.localeCompare(b.configKey)),
    [configs]
  );

  const handleCreate = () => {
    setEditing(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEdit = (record: ConfigRecord) => {
    setEditing(record);
    form.setFieldsValue({
      configKey: record.configKey,
      configValue: stringifyValue(record.configValue),
      description: record.description ?? ''
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const parsed = parseValue(values.configValue);

    setConfig(values.configKey, parsed, values.description ?? null);
    setIsModalOpen(false);
  };

  const columns: ColumnsType<ConfigRecord> = [
    {
      title: 'キー',
      dataIndex: 'configKey',
      key: 'configKey',
      width: 260
    },
    {
      title: '値',
      dataIndex: 'configValue',
      key: 'configValue',
      render: (value: unknown) => (
        <Text code style={{ whiteSpace: 'pre-wrap' }}>
          {stringifyValue(value)}
        </Text>
      )
    },
    {
      title: '説明',
      dataIndex: 'description',
      key: 'description',
      render: (value: string | undefined) => value ?? '-'
    },
    {
      title: '種別',
      dataIndex: 'isDefault',
      key: 'isDefault',
      width: 120,
      render: (value: boolean) =>
        value ? <Tag color="blue">デフォルト</Tag> : <Tag color="green">カスタム</Tag>
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            type="link"
            onClick={() => handleEdit(record)}
          >
            編集
          </Button>
          {!record.isDefault && (
            <Button
              icon={<DeleteOutlined />}
              type="link"
              danger
              loading={isDeleting}
              onClick={() =>
                Modal.confirm({
                  title: '設定を削除しますか？',
                  content: `${record.configKey} を削除します。`,
                  okType: 'danger',
                  okText: '削除',
                  cancelText: 'キャンセル',
                  onOk: () => deleteConfig(record.configKey)
                })
              }
            >
              削除
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Title level={3} style={{ margin: 0 }}>
            システム設定
          </Title>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => void refetch()} />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              新規設定
            </Button>
          </Space>
        </Space>

        <Table<ConfigRecord>
          rowKey="configKey"
          loading={isLoading}
          columns={columns}
          dataSource={sortedConfigs}
          pagination={false}
        />

        <Text type="secondary">
          設定値は JSON 形式で入力できます。単純な文字列の場合はそのまま記入してください。
        </Text>
      </Space>

      <Modal
        title={editing ? '設定を編集' : '設定を追加'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => void handleSubmit()}
        okButtonProps={{ loading: isSaving }}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="キー"
            name="configKey"
            rules={[
              { required: true, message: '設定キーを入力してください' },
              { max: 100, message: '100文字以内で入力してください' }
            ]}
          >
            <Input disabled={Boolean(editing)} placeholder="例: schedule.default_duration_minutes" />
          </Form.Item>

          <Form.Item
            label="値 (JSON 形式推奨)"
            name="configValue"
            rules={[{ required: true, message: '設定値を入力してください' }]}
          >
            <TextArea rows={6} placeholder='例: 60  または {"enabled": true}' />
          </Form.Item>

          <Form.Item label="説明" name="description">
            <Input placeholder="任意の説明" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
